/**
 * Clinic `activated_modules` helpers.
 *
 * Most modules are opt-out (missing key ⇒ enabled).
 * Opt-in modules (e.g. Vejlefjord Henvisninger) are disabled until explicitly enabled.
 */

/** Keys that must be explicitly set to true before the feature is available. */
export const OPT_IN_MODULE_KEYS = ['referrals', 'communicate'] as const;

export type ClinicModuleKey = string;

export function isOptInModule(moduleKey: string): boolean {
  return (OPT_IN_MODULE_KEYS as readonly string[]).includes(moduleKey);
}

/** Normalize JSONB `activated_modules` into a string→boolean map. */
export function readActivatedModules(value: unknown): Record<string, boolean> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const out: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    out[k] = Boolean(v);
  }
  return out;
}

/**
 * Whether a clinic module is enabled.
 * - Opt-in keys: enabled only when explicitly `true`
 * - All other keys: enabled unless explicitly `false`
 */
export function isClinicModuleEnabled(
  modules: Record<string, boolean> | null | undefined,
  moduleKey: string,
): boolean {
  const map = modules ?? {};
  if (isOptInModule(moduleKey)) {
    return map[moduleKey] === true;
  }
  return map[moduleKey] !== false;
}

/**
 * Sidebar visibility for a module-gated item.
 * Opt-in modules stay visible until clinic settings have loaded, so Korrespondance
 * is on the first paint after login instead of appearing only after navigation.
 */
export function isNavModuleVisible(
  modules: Record<string, boolean> | null | undefined,
  moduleKey: string,
  modulesKnown: boolean,
): boolean {
  if (!modulesKnown && isOptInModule(moduleKey)) {
    return true;
  }
  return isClinicModuleEnabled(modules, moduleKey);
}
